/**
 * Route permissions configuration
 * Defines which roles can access which routes
 */

export const USER_ROLES = {
  CUSTOMER: "customer",
  VENDOR: "vendor",
  RIDER: "rider",
  INDEPENDENT_RIDER: "independentRider",
  COMPANY_RIDER: "companyRider",
  COMPANY: "company",
  STARRIK: "admin",
};

/**
 * Route access matrix
 * Maps routes to allowed roles
 */
export const ROUTE_PERMISSIONS = {
  // Dashboard - All roles
  "/dashboard": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
    USER_ROLES.STARRIK,
  ],

  // Orders - All roles (but different components)
  "/orders": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    // USER_ROLES.RIDER,
    // USER_ROLES.INDEPENDENT_RIDER,
    // USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
    USER_ROLES.STARRIK,
  ],
  "/orders/:id": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    // USER_ROLES.RIDER,
    // USER_ROLES.INDEPENDENT_RIDER,
    // USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
    USER_ROLES.STARRIK,
  ],

  // Rider-specific routes
  "/active-orders": [
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
  ],
  "/my-running-order": [
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
  ],

  // Company routes
  "/riders": [USER_ROLES.COMPANY, USER_ROLES.STARRIK],
  "/bikes": [USER_ROLES.COMPANY, USER_ROLES.STARRIK],

  // Starrik admin routes
  "/users": [USER_ROLES.STARRIK],
  "/users/user/:role/:id": [USER_ROLES.STARRIK],
  "/employees": [USER_ROLES.STARRIK],
  "/transactions": [USER_ROLES.STARRIK],

  // Shared routes (all authenticated users)
  "/settings": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
    USER_ROLES.STARRIK,
  ],
  "/settings/*": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
    USER_ROLES.STARRIK,
  ],
  "/withdrawals": [
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
    USER_ROLES.VENDOR,
  ],
  "/referrals": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
  ],
  "/notifications": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
    USER_ROLES.COMPANY,
    USER_ROLES.STARRIK,
  ],
  "/my-cart": [USER_ROLES.CUSTOMER, USER_ROLES.VENDOR],
  "/checkout": [USER_ROLES.CUSTOMER, USER_ROLES.VENDOR],
  "/create-request": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
  ],
  "/create-request/*": [
    USER_ROLES.CUSTOMER,
    USER_ROLES.VENDOR,
    USER_ROLES.RIDER,
    USER_ROLES.INDEPENDENT_RIDER,
    USER_ROLES.COMPANY_RIDER,
  ],
};

/**
 * Check if a user role has access to a route
 * @param {string} pathname - The route pathname
 * @param {string} userRole - The user's role
 * @returns {boolean} Whether the user has access
 */
export const hasRouteAccess = (pathname, userRole) => {
  // Normalize pathname (remove trailing slash, handle dynamic segments)
  console.log(pathname, userRole);
  const normalizedPath = pathname.split("?")[0].replace(/\/$/, "") || "/";

  // Check exact match first
  if (ROUTE_PERMISSIONS[normalizedPath]) {
    return ROUTE_PERMISSIONS[normalizedPath].includes(userRole);
  }

  // Check pattern matches (e.g., /orders/:id)
  for (const [routePattern, allowedRoles] of Object.entries(
    ROUTE_PERMISSIONS
  )) {
    const patternRegex = new RegExp(
      "^" + routePattern.replace(/:[^/]+/g, "[^/]+") + "$"
    );
    if (patternRegex.test(normalizedPath)) {
      return allowedRoles.includes(userRole);
    }
  }

  // Default: allow if it's a general shared route
  // You may want to change this to false for stricter access control
  return true;
};

/**
 * Get allowed roles for a route
 * @param {string} pathname - The route pathname
 * @returns {string[]} Array of allowed roles
 */
export const getAllowedRoles = (pathname) => {
  const normalizedPath = pathname.split("?")[0].replace(/\/$/, "") || "/";

  if (ROUTE_PERMISSIONS[normalizedPath]) {
    return ROUTE_PERMISSIONS[normalizedPath];
  }

  // Check pattern matches
  for (const [routePattern, allowedRoles] of Object.entries(
    ROUTE_PERMISSIONS
  )) {
    const patternRegex = new RegExp(
      "^" + routePattern.replace(/:[^/]+/g, "[^/]+") + "$"
    );
    if (patternRegex.test(normalizedPath)) {
      return allowedRoles;
    }
  }

  return []; // Default to no access if not found
};
